import React, { useEffect, useMemo, useRef, type ReactNode, type RefObject } from 'react';
import { FormattedMessage } from 'react-intl';
import { MdAdd, MdSend } from 'react-icons/md';
import { Spinner, SPINNER_SIZE_SM } from '@aibuild/ui';
import { AttachmentTray, fromFiles } from '@components/Attachments';
import { AttachmentPreviewArea, CharacterCount, FileErrorText, HiddenFileInput } from '@components/Chat/Chat.styled';
import DragOverlay from '@components/DragOverlay/DragOverlay';
import type { UseFileAttachmentsResult } from '@hooks/useFileAttachments';
import { useFileDragDrop } from '@hooks/useFileDragDrop';
import { collectClipboardImages } from '@utils/clipboardFiles';
import { COUNTER_VISIBLE_RATIO } from './PromptComposer.constants';
import { Footer, InputField, Pill, Root, SendButton, UploadButton } from './PromptComposer.styled';

export interface PromptComposerProps {
  readonly value: string;
  readonly onChange: (text: string) => void;
  readonly onSubmit: () => void;
  readonly canSubmit: boolean;
  readonly busy?: boolean;
  readonly attachments: UseFileAttachmentsResult;
  readonly maxLength: number;
  readonly placeholder: string;
  readonly ariaLabel?: string;
  readonly attachLabel: string;
  readonly sendLabel: string;
  readonly inputRef?: RefObject<HTMLTextAreaElement | null>;
  readonly autoFocus?: boolean;
  readonly footer?: ReactNode;
  readonly dataTestId?: string;
}

const isSubmitKey = (event: React.KeyboardEvent<HTMLTextAreaElement>): boolean =>
  event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing;

const PromptComposer: React.FC<PromptComposerProps> = ({
  value,
  onChange,
  onSubmit,
  canSubmit,
  busy = false,
  attachments,
  maxLength,
  placeholder,
  ariaLabel,
  attachLabel,
  sendLabel,
  inputRef,
  autoFocus,
  footer,
  dataTestId = 'prompt-composer',
}) => {
  const ownInputRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = inputRef ?? ownInputRef;
  const fileInput = useRef<HTMLInputElement>(null);
  const { pendingFiles, fileError, remainingSlots, handleFileSelect, handleRemoveFile } = attachments;
  const items = useMemo(() => fromFiles(pendingFiles), [pendingFiles]);
  const { isDragOver } = useFileDragDrop(handleFileSelect, !busy);

  const sendDisabled = !canSubmit || busy;
  const attachDisabled = remainingSlots === 0 || busy;
  const showCounter = value.length >= maxLength * COUNTER_VISIBLE_RATIO;

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value, textareaRef]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!isSubmitKey(event)) return;
    event.preventDefault();
    if (!sendDisabled) onSubmit();
  };

  const onPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const images = collectClipboardImages(event.clipboardData);
    if (images.length === 0) return;
    event.preventDefault();
    handleFileSelect(images);
  };

  return (
    <Root data-testid={dataTestId}>
      <DragOverlay visible={isDragOver} />
      <HiddenFileInput
        ref={fileInput}
        type="file"
        multiple
        onChange={(event) => {
          handleFileSelect(event.target.files);
          event.target.value = '';
        }}
        data-testid={`${dataTestId}__file-input`}
      />
      {items.length > 0 && (
        <AttachmentPreviewArea>
          <AttachmentTray items={items} onRemove={handleRemoveFile} />
        </AttachmentPreviewArea>
      )}
      {fileError && (
        <FileErrorText role="alert" data-testid={`${dataTestId}__file-error`}>
          {fileError}
        </FileErrorText>
      )}
      <Pill>
        <UploadButton
          type="button"
          title={attachLabel}
          aria-label={attachLabel}
          onClick={() => fileInput.current?.click()}
          disabled={attachDisabled}
          data-testid={`${dataTestId}__attach`}
        >
          <MdAdd size={20} aria-hidden="true" />
        </UploadButton>
        <InputField
          ref={textareaRef}
          value={value}
          rows={1}
          maxLength={maxLength}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={placeholder}
          aria-label={ariaLabel}
          autoFocus={autoFocus}
          data-testid={`${dataTestId}__prompt`}
        />
        <SendButton
          type="button"
          title={sendLabel}
          aria-label={sendLabel}
          onClick={onSubmit}
          disabled={sendDisabled}
          data-testid={`${dataTestId}__send`}
        >
          {busy ? <Spinner size={SPINNER_SIZE_SM} label={sendLabel} /> : <MdSend size={20} aria-hidden="true" />}
        </SendButton>
      </Pill>
      {showCounter && (
        <CharacterCount $over={value.length >= maxLength} data-testid={`${dataTestId}__char-count`}>
          <FormattedMessage
            id="chat.input.characterCount"
            defaultMessage="{count} / {max} characters"
            values={{ count: value.length.toLocaleString(), max: maxLength.toLocaleString() }}
          />
        </CharacterCount>
      )}
      {footer && <Footer>{footer}</Footer>}
    </Root>
  );
};

export default PromptComposer;
